import { Checkbox } from "components/Form/components/Checkbox/Checkbox";
import { CheckboxGroup } from "components/Form/components/Checkbox/CheckboxGroup";
import { FormComponentProps } from "components/Form/components/FormComponentProps";
import { Input } from "components/Form/components/Input/Input";
import { InputComponentProps } from "components/Form/components/InputComponentProps";
import { ValueLabelPair } from "containers/ExecutionFilter/interfaces";
import React from "react";

interface CheckboxGroupProps {
    identifier: string;
    input: InputComponentProps<string[]>;
    attributes?: { row?: boolean; disabled?: boolean };
    options: ValueLabelPair[];
    currentValue: string[];
    onChange: (value) => (event: React.ChangeEvent<HTMLInputElement>) => void;
    // Opt-in per-option class hook: when provided, its return value is applied as
    // the className of that option's checkbox wrapper. Used by the logs Level
    // filter to tint the Warning/Error options with the same severity colours as
    // the log rows (reused tokens, see LogListFilter.scss). Returning undefined
    // leaves the option unstyled, so the other filters using this shared control
    // are unaffected.
    getOptionClassName?: (value: string) => string | undefined;
}

interface NestedCheckboxGroupProps {
    mainLabel: string;
    onChangeAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
    mainCheckboxValue?: boolean | null;
    identifier: CheckboxGroupProps["identifier"];
    input: CheckboxGroupProps["input"];
    options: ValueLabelPair[];
    onChange: CheckboxGroupProps["onChange"];
}

export const NestedCheckboxGroup = ({
    mainLabel,
    onChangeAll,
    mainCheckboxValue,
    identifier,
    input,
    options,
    onChange,
}: NestedCheckboxGroupProps) => (
    <Checkbox
        label={mainLabel}
        onChange={onChangeAll}
        value={mainCheckboxValue}
        className="checkbox-group__container"
    >
        <CheckboxGroupContainer
            identifier={identifier}
            input={input}
            options={options}
            currentValue={input.value}
            onChange={onChange}
        />
    </Checkbox>
);

export const CheckboxGroupContainer = ({
    identifier,
    input,
    attributes,
    options,
    currentValue,
    onChange,
    getOptionClassName,
}: CheckboxGroupProps) => {
    return (
        <CheckboxGroup {...input} {...attributes} aria-label={identifier}>
            {options.map((option) => (
                <Checkbox
                    key={option.value}
                    value={currentValue.includes(option.value)}
                    onChange={onChange(option.value)}
                    label={option.label}
                    size="small"
                    className={getOptionClassName?.(option.value)}
                />
            ))}
        </CheckboxGroup>
    );
};

export const CheckboxGroupFormComponent = ({
    input,
    meta,
    fieldProps,
    fieldAttr,
}: FormComponentProps) => {
    return (
        <Input fieldProps={fieldProps} fieldAttr={fieldAttr} input={input} meta={meta}>
            <CheckboxGroupContainer
                identifier={input.name}
                input={input}
                attributes={fieldAttr}
                options={fieldProps.options}
                currentValue={fieldProps.currentValue}
                onChange={fieldProps.onChange}
                getOptionClassName={fieldProps.getOptionClassName}
            />
        </Input>
    );
};
